import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { PhUsersThree, PhUserPlus, PhChatText, PhFileText } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import SectionPanel from 'tio-ui/components/section-panel';
import Card from 'tio-ui/components/card';
import { getFieldValue } from '../../../utils/tas/program-builder/sections';
import { workflowsFor } from '../../../utils/tas/program-builder/workflows';
import type IntlService from 'ember-intl/services/intl';
import type { ProgramTypeMeta } from '../../../utils/tas/program-builder/sections';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];

interface ApproverEntry {
  role?: string;
  employeeId?: string;
  ordinal?: number;
}

interface TemplateItem {
  name: string;
  text: string;
}

/** SNAKE_CAPS enum name → Title Case (same as the builder's approver display). */
function humanizeEnumName(name: string): string {
  return name
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface ReadoutApprovalsPanelSignature {
  Args: {
    fields: FieldsSignature;
    typeMeta: ProgramTypeMeta;
    model: TASProgramTemplateModel;
    /** Reveal internal FIELD_NAMES badges (admin builder's devops toggle). */
    showFieldNames?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Read-only Approvals & Workflows: one card per workflow with its ordered
 * approver chain, the response templates approvers pick from (record-based,
 * falling back to the legacy single message), and the step's terms &
 * conditions (with links to attached policy PDFs). AI/analyzer settings are
 * deliberately omitted from the employer-facing view.
 */
export default class ReadoutApprovalsPanel extends Component<ReadoutApprovalsPanelSignature> {
  @service declare intl: IntlService;

  /** Response-template records live on the workflow's *_TEXT field as
   * tas-field-options (`label` = template name, `value` = message text); the
   * legacy single value backs a template when no records exist. */
  private templates(field: string): TemplateItem[] {
    const records = (this.args.model.tasFieldOptions ?? [])
      .filter((option) => option.fieldName === field)
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
      .map((option) => ({
        name: String(option.label ?? '').trim(),
        text: String(option.value ?? '').trim(),
      }))
      .filter((item) => item.name || item.text);
    if (records.length) {
      return records;
    }
    const legacy = String(getFieldValue(this.args.fields, field) ?? '').trim();
    return legacy ? [{ name: '', text: legacy }] : [];
  }

  get workflowCards() {
    const config = this.args.fields as unknown as Record<
      string,
      { required?: boolean; values?: unknown[] } | undefined
    >;
    const type = String(getFieldValue(this.args.fields, 'TYPE') ?? '');
    return workflowsFor(type).map((def) => {
      const entry = config[def.workflowField] ?? {};
      const approvers = ((entry.values ?? []) as ApproverEntry[])
        .slice()
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
      return {
        def,
        isEvidence: def.key === 'evidence',
        deadline: def.autoApproveDaysField ? this.calendarDays(def.autoApproveDaysField) : null,
        required: Boolean(entry.required),
        approverRows: approvers.map((approver, index) => ({
          number: index + 1,
          role: approver.role ? humanizeEnumName(approver.role) : '—',
          employeeId: (approver.employeeId ?? '').trim(),
        })),
        // Only groups with actual content get a card; a workflow with none
        // renders a single "none configured" line instead of empty boxes.
        templateGroups: [
          {
            title: this.intl.t('program_builder.approvals.approval'),
            fieldName: def.approveTextField,
            items: this.templates(def.approveTextField),
          },
          {
            title: this.intl.t('program_builder.approvals.revision'),
            fieldName: def.reviseTextField,
            items: this.templates(def.reviseTextField),
          },
          {
            title: this.intl.t('program_builder.approvals.rejection'),
            fieldName: def.rejectTextField,
            items: this.templates(def.rejectTextField),
          },
        ].filter((group) => group.items.length),
        conditions: (this.args.model.conditions ?? []).filter(
          (condition) => condition.province === def.conditionProvince
        ),
      };
    });
  }

  /** "N calendar days" for a step with a deadline rule; an em dash while the field is blank. */
  private calendarDays(field: string): string {
    const raw = String(getFieldValue(this.args.fields, field) ?? '').trim();
    return raw
      ? this.intl.t('program_builder.summary.value.calendar_days', { count: Number(raw) })
      : '—';
  }

  /** Localized Yes/No for a boolean setting — a real answer, never an em dash. */
  private yesNo(on: boolean): string {
    return this.intl.t(
      on ? 'program_builder.summary.value.yes' : 'program_builder.summary.value.no'
    );
  }

  /** Yes/No when the field is present; an em dash only when it's genuinely absent. */
  get autoFulfillValue(): string {
    const raw = getFieldValue(this.args.fields, 'AUTO_FULFILL_PAST_COURSE_END_DATE');
    if (raw == null || raw === '') return '—';
    return this.yesNo(Boolean(raw));
  }

  get invoiceDateVisibleValue() {
    const config = this.args.fields as unknown as Record<
      string,
      { visible?: boolean; required?: boolean } | undefined
    >;
    return this.yesNo(Boolean(config['INVOICE_DATE']?.visible));
  }

  get invoiceDateRequiredValue() {
    const config = this.args.fields as unknown as Record<
      string,
      { visible?: boolean; required?: boolean } | undefined
    >;
    return this.yesNo(Boolean(config['INVOICE_DATE']?.required));
  }

  <template>
    <div class="space-y-5" ...attributes>
      {{#each this.workflowCards key="@index" as |card|}}
        <SectionPanel
          @icon={{PhUsersThree}}
          @title={{t card.def.nameKey}}
          @iconColor="violet"
          data-test-readout-workflow={{card.def.key}}
        >
          <:trailing>
            {{#if card.required}}
              <Chip @size="sm" @intent="primary" @appearance="faded">{{t
                  "program_builder.approvals.required"
                }}</Chip>
            {{/if}}
          </:trailing>
          <:body>
            <p class="-mt-1 mb-3 text-xs tio-fg-muted">{{t card.def.descKey}}</p>

            <div class="tio-text-overline mb-1.5 tio-fg-muted">
              {{t "program_builder.approvals.approver_chain_heading"}}
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono normal-case tio-text-micro tio-fg-brand"
                >{{card.def.workflowField}}</code>
              {{/if}}
            </div>
            {{#if card.approverRows.length}}
              <div class="space-y-1.5">
                {{#each card.approverRows as |row|}}
                  <div class="tio-row-tight text-sm">
                    <span
                      class="tio-row-center h-6 w-6 shrink-0 rounded-full tio-bg-sunken tio-text-xs-semibold tio-fg-muted"
                    >{{row.number}}</span>
                    <span class="font-medium">{{row.role}}</span>
                    {{#if row.employeeId}}
                      <span class="tio-fg-muted">· {{row.employeeId}}</span>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            {{else}}
              <p class="mb-0 text-sm tio-fg-faint">—</p>
            {{/if}}

            <div class="mt-4">
              <div class="tio-text-overline mb-1.5 tio-fg-muted">
                {{t "program_builder.approvals.response_templates"}}
              </div>
              {{#if card.templateGroups.length}}
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {{#each card.templateGroups as |group|}}
                    <Card @radius="compact" @elevation="none" @padding="xs">
                      <div class="tio-row-snug tio-text-xs-medium tio-fg-secondary">
                        <PhChatText @size={{14}} class="shrink-0 tio-fg-muted" />
                        {{group.title}}
                      </div>
                      {{#if @showFieldNames}}
                        <code
                          class="mt-0.5 block break-words font-mono tio-text-micro tio-fg-brand"
                        >{{group.fieldName}}</code>
                      {{/if}}
                      <div class="mt-2 space-y-2">
                        {{#each group.items as |item|}}
                          <div class="text-sm">
                            {{#if item.name}}
                              <div class="tio-text-xs-semibold">{{item.name}}</div>
                            {{/if}}
                            <p class="mb-0 whitespace-pre-line text-xs tio-fg-muted">
                              {{item.text}}
                            </p>
                          </div>
                        {{/each}}
                      </div>
                    </Card>
                  {{/each}}
                </div>
              {{else}}
                <p
                  class="mb-0 text-sm tio-fg-muted"
                  data-test-readout-no-templates={{card.def.key}}
                >
                  {{t "program_builder.readout.no_response_templates"}}
                </p>
              {{/if}}
            </div>

            {{#if card.conditions.length}}
              <div class="mt-4">
                <div class="tio-text-overline mb-1.5 tio-fg-muted">
                  {{t "program_builder.approvals.terms_for_step"}}
                </div>
                <div class="space-y-2">
                  {{#each card.conditions as |condition|}}
                    <div class="rounded-lg border tio-border-default px-3 py-2">
                      <div class="tio-row">
                        <PhFileText @size={{16}} class="shrink-0 tio-fg-muted" />
                        <span class="tio-fill-min text-sm tio-fg-secondary">
                          {{if
                            condition.prompt
                            condition.prompt
                            (t "program_builder.terms_table.untitled_term")
                          }}
                        </span>
                        {{#if condition.hasPdf}}
                          <a
                            href={{condition.pdfUrl}}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="tio-text-xs-medium tio-fg-brand hover:underline"
                          >
                            {{t "program_builder.terms_table.pdf_badge"}}
                          </a>
                        {{/if}}
                      </div>
                      {{! Typed policy text (authored instead of / alongside a
                      PDF) — collapsed since it can run to pages. }}
                      {{#if condition.fullText}}
                        <details class="mt-1 pl-6">
                          <summary
                            class="cursor-pointer tio-text-xs-medium tio-fg-brand"
                            data-test-readout-terms-text
                          >
                            {{t "program_builder.readout.view_terms_text"}}
                          </summary>
                          <p class="mb-0 mt-1 whitespace-pre-line text-xs tio-fg-muted">
                            {{condition.fullText}}
                          </p>
                        </details>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>
              </div>
            {{/if}}

            {{#if card.deadline}}
              <div
                class="mt-4 tio-row-between gap-3 border-t tio-border-subtle pt-3 tio-text-sm-normal"
              >
                <span class="tio-fg-muted">{{t
                    "program_builder.approvals.deadline_readout_label"
                  }}</span>
                <span
                  class="text-right tio-text-sm-medium {{if (eq card.deadline '—') 'tio-fg-faint'}}"
                  data-test-readout-auto-approve-days
                >{{card.deadline}}</span>
              </div>
            {{/if}}

            {{#if card.isEvidence}}
              <div class="mt-4 tio-row-between gap-3 border-t tio-border-subtle pt-3 text-sm">
                <span class="tio-fg-muted">{{t
                    "program_builder.approvals.auto_fulfill_label"
                  }}</span>
                <span
                  class="text-right font-medium
                    {{if (eq this.autoFulfillValue '—') 'tio-fg-faint'}}"
                  data-test-readout-auto-fulfill
                >{{this.autoFulfillValue}}</span>
              </div>
            {{/if}}
          </:body>
        </SectionPanel>
      {{/each}}

      {{#unless this.workflowCards.length}}
        <SectionPanel
          @icon={{PhUsersThree}}
          @title={{t "program_builder.approvals.approvals_title"}}
          @iconColor="violet"
        >
          <:body>
            <p class="mb-0 text-sm tio-fg-muted">
              {{t "program_builder.approvals.no_workflows"}}
              {{! Only claim Tracking-Only when that's actually the reason —
              an unset/unknown program type also yields zero workflows. }}
              {{#if @typeMeta.trackingOnly}}
                <Chip @size="sm" @appearance="faded">{{t
                    "program_builder.approvals.tracking_only"
                  }}</Chip>
              {{/if}}
            </p>
          </:body>
        </SectionPanel>
      {{/unless}}

      <SectionPanel
        @icon={{PhUserPlus}}
        @title={{t "program_builder.readout.invoice_inputs_title"}}
        @iconColor="gray"
      >
        <:body>
          <p class="mb-3 text-xs tio-fg-muted">
            {{t "program_builder.approvals.invoice_date_help"}}
            {{#if @showFieldNames}}
              <code class="ml-1 font-mono tio-text-micro tio-fg-brand">INVOICE_DATE</code>
            {{/if}}
          </p>
          <div class="space-y-1 text-sm">
            <div class="tio-row-between gap-3">
              <span class="tio-fg-muted">{{t "program_builder.readout.invoice_visible"}}</span>
              <span class="text-right font-medium">{{this.invoiceDateVisibleValue}}</span>
            </div>
            <div class="tio-row-between gap-3">
              <span class="tio-fg-muted">{{t "program_builder.readout.invoice_required"}}</span>
              <span class="text-right font-medium">{{this.invoiceDateRequiredValue}}</span>
            </div>
          </div>
        </:body>
      </SectionPanel>
    </div>
  </template>
}
